import { cn } from "@/shared/lib/cn";

export interface StepperProps {
  /** Etiquetas de los pasos, en orden. Su longitud es el total. */
  steps: readonly string[];
  /** Paso actual, base 0. */
  current: number;
  /** Nombre de la región para lectores de pantalla. */
  label?: string;
  className?: string;
}

/** Lo que se le dice a un lector de pantalla sobre cada paso. */
const STATE_TEXT = {
  done: "completado",
  current: "paso actual",
  todo: "pendiente",
} as const;

/**
 * El indicador de progreso de un wizard.
 *
 * ── Por qué barras y no círculos numerados ──────────────────────────
 * Vive DENTRO de la card de vidrio del acceso, encima de un formulario.
 * Una fila de círculos con número y línea entre ellos come el doble de
 * alto y compite con el título por ser lo primero que se mira; el registro
 * no es un envío que se rastrea, es un trámite de tres minutos y su
 * progreso debe informar sin protagonizar. Tres segmentos de 6 px hacen el
 * mismo trabajo en una sola línea.
 *
 * ── Lo que se dice y lo que se ve ───────────────────────────────────
 * "Paso 2 de 3" es texto REAL, no un `aria-label`, y va en una región
 * viva: al cambiar de paso se anuncia solo, que es la única señal que
 * tiene quien no ve las barras llenarse. La lista es un `<ol>` con
 * `aria-current="step"` en el actual y el estado de cada paso en texto
 * accesible; los rótulos de abajo —que solo caben en escritorio— van
 * `aria-hidden` para no leer dos veces lo mismo.
 */
export function Stepper({
  steps,
  current,
  label = "Progreso",
  className,
}: StepperProps) {
  const total = steps.length;
  const safeCurrent = Math.min(Math.max(current, 0), total - 1);

  return (
    <nav aria-label={label} className={cn("grid gap-2", className)}>
      <div className="flex items-baseline justify-between gap-3">
        <p
          aria-live="polite"
          className="text-[11px] font-semibold uppercase tracking-ds-label-wide text-ds-violet-pop"
        >
          Paso {safeCurrent + 1} de {total}
        </p>
        {/*
          El rótulo del paso ACTUAL, en la misma línea que la cuenta. Antes
          había además una fila con los tres rótulos bajo las barras: dos
          renglones de texto diminuto para decir lo que ya dicen la cuenta
          y las barras, y los pasos futuros iban en `ds-ink-4` — 2.97:1,
          por debajo del mínimo legible. Con las barras a la vista, saber
          en cuál vas y cómo se llama basta; los nombres de los que faltan
          aportaban poco y costaban el alto que necesita el paso 1 para
          caber en pantalla. Los tres siguen anunciándose en `sr-only`.
        */}
        <p aria-hidden="true" className="truncate text-[11px] text-ds-ink-3">
          {steps[safeCurrent]}
        </p>
      </div>

      <ol className="flex items-center gap-1.5">
        {steps.map((stepLabel, index) => {
          const state =
            index < safeCurrent ? "done" : index === safeCurrent ? "current" : "todo";

          return (
            <li
              key={stepLabel}
              className="flex-1"
              aria-current={state === "current" ? "step" : undefined}
            >
              <span className="sr-only">{`${stepLabel}: ${STATE_TEXT[state]}`}</span>
              <span
                aria-hidden="true"
                className={cn(
                  "block h-1.5 rounded-ds-pill transition-colors duration-300 motion-reduce:transition-none",
                  state === "todo" ? "bg-ds-lilac/40" : "bg-ds-violet-pop",
                  // El actual se distingue del ya recorrido sin cambiar de
                  // color: un halo, que no depende de percibir dos violetas.
                  state === "current" && "shadow-ds-glow",
                )}
              />
            </li>
          );
        })}
      </ol>
    </nav>
  );
}
